// src/pages/Home.js
import React from 'react'
import Background from '../components/Background'
import AgentAnimation from '../components/AgentAnimation'
import { Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Grid,
  Container,
  Card,
  Stack,
  GlobalStyles,
  useTheme,
} from '@mui/material'
import { styled } from '@mui/system'
import { motion } from 'framer-motion'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import FlashOnIcon from '@mui/icons-material/FlashOn'
import CodeIcon from '@mui/icons-material/Code'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import InsightsIcon from '@mui/icons-material/Insights'
import LockIcon from '@mui/icons-material/Lock'
import PolicyIcon from '@mui/icons-material/Policy'
import LanIcon from '@mui/icons-material/Lan'
import SearchIcon from '@mui/icons-material/Search'
import DemoImg from '../assets/demo.png' 
import PersonIcon from '@mui/icons-material/Person'


const ACCENT = '#467EE5'
const ACCENT2 = '#DA605B'
const ACCENT3 = '#00C851' 

// Wrapper for whole page
const MainWrapper = styled(Box)({
  position: 'relative',
  bgcolor: '#000',
  color: '#fff',
  overflow: 'hidden',
})

// Section heading style
const SectionTitle = styled(motion(Typography))({
  fontFamily: '"Poppins", sans-serif',
  fontWeight: 700,
  fontSize: '2.25rem',
  lineHeight: 1.3,
  color: '#fff',
  textAlign: 'center',
  marginBottom: '2rem',
  textShadow: `0 0 8px ${ACCENT2}22`,
})

const ConnectorLine = styled(motion.div)({
  width: '2px',
  margin: '0.5rem 0',
});
const futureTitles = [
  'Standard Local API',
  'Central Agent Registry',
  'Edge Execution',
  'Self‑Healing Connectors',
]

const FlowContainer = styled(Box)({
  backgroundColor: 'rgba(255,255,255,0.05)',
  borderRadius: 8,
  padding: '1.5rem',
  '&.flow-container': {
    '@media (max-width: 600px)': {
      padding: '0.75rem',
    }
  }
});

const StepDot = styled(motion.div)({
  width: 64,
  height: 64,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: `0 0 0 6px rgba(0,0,0,0.1)`,
  '@media (max-width: 600px)': {
    width: 52,
    height: 52,
  }
});
const CARD_WIDTH = 380
const MobileResponsiveWrapper = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    // Hero tweaks
    '& .hero-title': {
      fontSize: '2.5rem !important',
      paddingLeft: '1rem !important',
      paddingRight: '1rem !important',
      textAlign: 'center !important',
      display: 'flex !important',
      flexDirection: 'column !important',
      alignItems: 'center !important',
      justifyContent: 'center !important',
      marginTop: '2rem !important',
    },
    '& .hero-subtitle': {
      fontSize: '1.1rem !important',
      paddingLeft: '1rem !important',
      paddingRight: '1rem !important',
    },
    '& .hero-buttons': {
      paddingLeft: '1rem !important',
      paddingRight: '1rem !important',
      gap: '1rem !important',
      alignItems: 'center !important',
      justifyContent: 'center !important',
      width: '100% !important',
      display: 'flex !important',
      marginTop: '2rem !important',
    },
    '& .hero-buttons .MuiButton-root': {
      width: '100% !important',
      maxWidth: '280px !important',
      marginTop: '0 !important',
      marginBottom: '0 !important',
      height: '48px !important',
      fontSize: '1rem !important',
      fontWeight: '700 !important',
      borderRadius: '999px !important',
      minHeight: '48px !important',
    },

    // Demo & features
    '& .demo-image': {
      maxWidth: '300px !important',
    },
    '& .feature-grid': {
      gridTemplateColumns: '1fr !important',
      gap: '1rem !important',
    },
    '& .feature-card': {
      width: '100% !important',
      height: 'auto !important',
      minHeight: '160px !important',
      padding: '1rem !important',
      maxWidth: '200px !important',
    },

    // Flows
    '& .flow-container': {
      maxWidth: '100% !important',
      padding: '0.75rem !important',
    },

   // hide the desktop header
   '& .comparison-header': {
    display: 'none !important',
  },

  // stack rows & add gap
  '& .comparison-row': {
    flexDirection: 'column !important',
    alignItems: 'stretch !important',
    marginBottom: '5rem !important', 
  },

  // rotate arrow & tighten spacing
  '& .comparison-row .comparison-arrow': {
    transform: 'rotate(90deg) !important',
    margin: '0.01rem auto !important',
  },

  // allow pseudo-labels to overflow the card
  '& .comparison-row > .comparison-card': {
    position: 'relative',
    overflow: 'visible !important',
  },

  // — Users labels (red) —
  '& .user-comparison .comparison-card:first-child::before': {
    content: '"Before Scout"',
    display: 'block',
    position: 'absolute',
    top: '-1.5rem',
    left: 0,
    fontSize: '1.1rem',
    fontWeight: 700,
    color: ACCENT2,
    marginBottom: '0.2rem',
  },
  '& .user-comparison .comparison-card:last-child::before': {
    content: '"With Scout"',
    display: 'block',
    position: 'absolute',
    top: '-1.5rem',
    left: 0,
    fontSize: '1.1rem',
    fontWeight: 700,
    color: ACCENT2,
    marginBottom: '0.5rem',
  },
  '& .user-comparison .comparison-card:first-child': {
    marginBottom: '0.1rem !important',
  },
  '& .user-comparison .MuiTypography-h3': {
    marginBottom: '2rem !important',
  },
  '& .user-comparison .comparison-card:last-child': {
    marginTop: '0.1rem !important',
  },

  // — Developers labels (blue) —
  '& .dev-comparison .comparison-card:first-child::before': {
    content: '"Before Scout"',
    display: 'block',
    position: 'absolute',
    top: '-1.5rem',
    left: 0,
    fontSize: '1.1rem',
    fontWeight: 700,
    color: ACCENT,
    marginBottom: '0.2rem',
  },
  '& .dev-comparison .comparison-card:last-child::before': {
    content: '"With Scout"',
    display: 'block',
    position: 'absolute',
    top: '-1.5rem',
    left: 0,
    fontSize: '1.1rem',
    fontWeight: 700,
    color: ACCENT,
    marginBottom: '0.5rem',
  },
  '& .dev-comparison .comparison-card:first-child': {
    marginBottom: '0.1rem !important',
  },
  '& .dev-comparison .MuiTypography-h3': {
    marginBottom: '2rem !important',
  },
  '& .dev-comparison .comparison-card:last-child': {
    marginTop: '0.11rem !important',
  },


    // Built-in Agents & Waitlist
    '& .agent-card': {
      width: '100% !important',
      padding: '1.25rem !important',
      height: '200px !important',
      minHeight: '200px !important',
      maxWidth: '280px !important',
    },
    '& .section-title': {
      fontSize: '1.75rem !important',
    },
    '& .waitlist-banner': {
      paddingLeft: '1rem !important',
      paddingRight: '1rem !important',
    },
    '& .waitlist-button': {
      width: '100% !important',
      maxWidth: '280px !important',
      marginTop: '0.5rem !important',
    },
  },
}));



// Data arrays
const howItWorksUsers = [
  {
    icon: <FolderOpenIcon />,
    title: 'Ingest your data',
    desc: 'Connect files, apps, and clouds to build a private profile of what makes you you.',
  },
  {
    icon: <SmartToyIcon />,
    title: 'Pick (or request) an agent',
    desc: 'Browse pre‑built personalized agents or request one to automate any task.',
  },
  {
    icon: <FlashOnIcon />,
    title: 'Automate (locally)',
    desc: `Agents run on‑device in a sandbox. \nNo cloud, no data leaks.`,
  },
]

const howItWorksDevs = [
  { icon: <CodeIcon />, title: 'Build with our SDK', desc: 'No backend required. Built-in auth and data connectors let you focus on building.' },
  {
    icon: <AccountTreeIcon />,
    title: 'Query the context layer',
    desc: 'Use a secure API that respects declared permissions.',
  },
  { icon: <FlashOnIcon />, title: 'Get paid', desc: 'Earn when users install your agents.' },
]

const whyDifferent = [
  {
    title: 'Knows your workflow',
    desc: 'Agents act in context using your private knowledge graph.',
    icon: InsightsIcon,
  },
  {
    title: 'Granular permissions',
    desc: 'You control exactly what each agent can see and do.',
    icon: LockIcon,
  },
  {
    title: 'Plug‑and‑play',
    desc: 'Install agents in one click. \nNo setup or code required.',
    icon: FlashOnIcon,
  },
  {
    title: 'Zero internet by default',
    desc: 'Agents can’t talk to the cloud unless you explicitly allow it.',
    icon: PolicyIcon,
  },
]


const userFacingProblems = [
  {
    title: 'Data‑Security & Privacy',
    past: 'Cloud agents can expose your private files and credentials.',
    futureTitle: 'Local Sandbox',
    future: 'Agents run in a secure local sandbox so your data never leaves your machine.',
  },
  {
    title: 'Complex Setup',
    past: 'Most agents require scripts, tokens, or integrations to get started.',
    futureTitle: 'Plug & Play',
    future: 'Install secure plug-and-play agents in one click.',
  },

  {
    title: 'Personalization',
    past: 'Agents don’t know your files, tools, preferences, or the nature of your work.',
    futureTitle: 'Contextual Agents',
    future: 'Scout agents use your personal profile to tailor tasks to your workflow.',
  },
 
  {
    title: 'Trust & Control',
    past: 'Giving agents full control over your apps is scary and risky.',
    futureTitle: 'Permissioned Actions',
    future: 'Agents make requests; Scout only executes them with your explicit approval.',
  },
]

const developerFacingProblems = [
  {
    title: 'Integration Overload',
    past: 'Writing and maintaining custom connectors breaks when APIs change.',
    futureTitle: 'Standard Local API',
    future: 'Connect once via a unified local API and shared knowledge graph.',
  },
  {
    title: 'Scalability & Reliability',
    past: 'Cloud APIs slow down or fail under heavy load.',
    futureTitle: 'Edge Execution',
    future: 'Your agents run on‑device so they stay fast and reliable at any scale.',
  },
  {
    title: 'Context Fragmentation',
    past: 'Separate agents have siloed memory, leading to conflicting results and duplicated work.',
    futureTitle: 'Shared Knowledge Graph',
    future: 'All agents read and write to one single on-disk graph for consistent context.',
  },
  {
    title: 'Observability & Debugging',
    past: 'Logs spread across LLM, RAG and gateways make debugging a scavenger hunt.',
    futureTitle: 'Unified Tracing UI',
    future: 'See every action in one local trace log so you can pinpoint failures instantly.',
  },
]

const coreProblems = [
  {
    title: 'Data‑leak Risk',
    past: 'Cloud‑based agents can expose your private files and credentials.',
    futureTitle: 'Local Sandbox',
    future: 'All processing runs in a secure local sandbox so your data never leaves your machine.',
  },
  {
    title: 'Integration Overload',
    past: 'Connecting to multiple APIs takes time and often breaks when services change.',
    futureTitle: 'Standard Local API',
    future: 'Connect once via a unified local API and shared knowledge graph.',
  },
  {
    title: 'Context Fragmentation',
    past: 'Multiple agents lack shared context, causing inconsistent behavior.',
    futureTitle: 'Shared Knowledge Graph',
    future: 'All agents operate on one unified graph for consistent and improved context.',
  },
  {
    title: 'Scalability Bottlenecks',
    past: 'Centralized APIs hit rate limits and outages under heavy use.',
    futureTitle: 'Edge Execution',
    future: 'Agents run on‑device, eliminating central bottlenecks.',
  },
  {
    title: 'Poor Observability',
    past: 'Logs scattered across LLM, RAG and API layers hinder debugging.',
    futureTitle: 'Unified Tracing UI',
    future: 'View all agent actions in one local trace log for easy debugging.',
  },
  {
    title: 'Service Discovery Chaos',
    past: 'Hunting through repos, marketplaces and blogs wastes time.',
    futureTitle: 'Central Agent Registry',
    future: 'Browse agents by category, rating and semantic search.',
  },
]

// Static card for “What Scout Fixes”
function StaticCard({ icon, title, problem, solution }) {
  return (
    <Card
      variant="outlined"
      sx={{
        bgcolor: '#1E1E1E',
        border: '1px solid #444',
        boxShadow: `0 4px 20px ${ACCENT2}55`,
        borderRadius: 2,
        width: '100%',
        maxWidth: 280,
        mx: 'auto',
        p: 3,
        minHeight: 360,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Box sx={{ fontSize: 48, color: ACCENT2 }}>
        {React.cloneElement(icon, { sx: { fontSize: 48, color: ACCENT2 } })}
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mt: 2, textAlign: 'center' }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: '#c9d1d9', mt: 2, textAlign: 'center' }}>
        <strong>Problem:</strong> {problem}
      </Typography>
      <Typography variant="body2" sx={{ color: '#c9d1d9', mt: 2, textAlign: 'center' }}>
        <strong>Solution:</strong> {solution}
      </Typography>
    </Card>
  )
}

export default function Home() {
  const theme = useTheme();
      return (
      <MobileResponsiveWrapper>
        <MainWrapper>
          <GlobalStyles styles={{ html: { scrollBehavior: 'smooth' } }} />
          <Background />

          {/* Hero Section */}
          <Box
            sx={{
              position: 'relative',
              overflow: 'hidden',
              px: 3,
              py: { xs: 6, md: 16 },
              minHeight: '85vh',
              textAlign: 'center',
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
        <AgentAnimation />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Typography 
            variant="h1" 
            fontFamily='"Poppins", sans-serif' 
            className="hero-title"
            sx={{ fontWeight: 800, mb: 2, lineHeight: 1.2 }}
          >
            Your Local <br /> AI Agent Store
          </Typography>
          <Typography
            variant="h5"
            color="text.secondary"
            className="hero-subtitle"
            sx={{ mb: 6, maxWidth: 600, mx: 'auto' }}
          >
            Download personalized agents to automate your desktop workflows while protecting your data.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              justifyContent="center" 
              spacing={2}
              className="hero-buttons"
            >
            <Button
              variant="outlined"
              size="large"
              onClick={() => {
                document.getElementById('features-section').scrollIntoView({ 
                  behavior: 'smooth' 
                });
              }}
              startIcon={<ArrowDownwardIcon />}
              sx={{
                px: { xs: 3, sm: 4 },
                py: { xs: 1.25, sm: 1.5 },
                fontWeight: 700,
                borderRadius: '999px',
                borderColor: ACCENT,
                color: ACCENT,
                fontSize: { xs: '0.9rem', sm: '1rem' },
                '&:hover': { bgcolor: `${ACCENT2}10`, borderColor: ACCENT2, color: ACCENT2 },
              }}
            >
              Learn More
            </Button>
            <Button
              component={RouterLink}
              to="/waitlist"
              variant="contained"
              size="large"
              sx={{
                px: { xs: 3, sm: 4 },
                py: { xs: 1.25, sm: 1.5 },
                fontWeight: 700,
                borderRadius: '999px',
                bgcolor: ACCENT,
                fontSize: { xs: '0.9rem', sm: '1rem' },
                '&:hover': { bgcolor: '#3b6cce' },
              }}
            >
              Join the Waitlist
            </Button>
            </Stack>
          </Box>
        </Container>
      </Box>{/* How It Works */}
      {/* Scout – Coming Soon! */}
      <Box
        id="features-section"
        component={motion.div}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.3 }}
        viewport={{ once: true, margin: '-100px 0px' }}
        sx={{
          py: { xs: 8, md: 12 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Container maxWidth="lg">
        <SectionTitle
  component={motion.h3}
  initial={{ opacity: 0, y: 10 }}
  whileInView={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.6, delay: 0.5 }}
  viewport={{ once: true }}
  variant="h3"
  align="center"
  sx={{
    WebkitTextStroke: `1px ${ACCENT2}`,
    WebkitTextFillColor: '#fff',
    color: 'transparent',
    mb: 6,
  }}
>
  Features
</SectionTitle>

          <Grid
            container
            spacing={{ xs: 3, md: 4 }}
            justifyContent="center"
            alignItems="center"
            sx={{ flexWrap: { xs: 'wrap', md: 'nowrap' } }}
          >
            {/* Demo Image */}
            <Grid
              item
              xs={12}
              md={6}
              component={motion.div}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              viewport={{ once: true }}
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              <Box
                component="img"
                src={DemoImg}
                alt="App Demo"
                sx={{
                  width: '100%',
                  maxWidth: { xs: 400, sm: 500, md: 600 },
                  borderRadius: 1.6,
                  border: `2px solid ${ACCENT2}`,
                  boxShadow: '0 12px 36px rgba(0,0,0,0.6)',
                }}
              />
            </Grid>

            {/* Feature Cards (2×2 grid, each 240×240) */}
            <Grid item xs={12} md={6}>
              <Box
                component={motion.div}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={{ visible: { transition: { staggerChildren: 0.2 } } }}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(2, 1fr)' },
                  gap: { xs: 2, sm: 3 },
                  justifyItems: 'center',
                }}
              >
                {whyDifferent.map((item, i) => (
                  <Card
                    key={i}
                    component={motion.div}
                    variants={{
                      hidden: { opacity: 0, y: 20 },
                      visible: { opacity: 1, y: 0 },
                    }}
                    transition={{ duration: 0.6 }}
                    variant="outlined"
                    className="feature-card"
                    sx={{
                      width: { xs: '100%', sm: 240 },
                      height: { xs: 'auto', sm: 275 },
                      minHeight: { xs: 240, sm: 275 },
                      bgcolor: '#111',
                      borderColor: '#222',
                      borderRadius: 2,
                      boxShadow: `0 4px 12px ${ACCENT2}33`,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      p: { xs: 2.5, sm: 2 },
                    }}
                  >
                    <Box sx={{ fontSize: 36, color: ACCENT2, mb: 2 }}>
                      <item.icon sx={{ fontSize: 36 }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                      {item.desc}
                    </Typography>
                  </Card>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* How It Works */}
      <Box sx={{ py: { xs: 8, md: 12 }, position: 'relative', zIndex: 1 }}>
        <Container maxWidth="lg">
        <SectionTitle
  component={motion.h3}
  initial={{ opacity: 0, y: 20 }}
  whileInView={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.6, ease: 'easeOut' }}
  viewport={{ once: true }}
  variant="h3"
  align="center"
  sx={{
    WebkitTextStroke: `1px ${ACCENT2}`,
    WebkitTextFillColor: '#fff',
    color: 'transparent',
    mb: 6,
  }}
>
  How It Works
</SectionTitle>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: { xs: 3, md: 4 },
              justifyContent: 'center',
            }}
          >
            {/* Users Flow */}
            <FlowContainer className="flow-container" sx={{ maxWidth: { xs: '100%', md: 360 } }}>
              <Typography
                component={motion.h5}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                variant="h5"
                align="center"
                sx={{
                  color: '#fff',
                  WebkitTextStroke: `1px ${ACCENT}`,
                  WebkitTextFillColor: '#fff',
                  fontWeight: 700,
                  mb: 2,
                }}
              >
                For Users
              </Typography>

              <Stack alignItems="center">
                {howItWorksUsers.map((step, i) => (
                  <React.Fragment key={i}>
                    <StepDot
                      component={motion.div}
                      initial={{ scale: 0, opacity: 0 }}
                      whileInView={{ scale: 1, opacity: 1 }}
                      transition={{
                        type: 'spring',
                        bounce: 0.3,
                        duration: 0.8,
                        delay: i * 0.3 + 0.4,
                      }}
                      viewport={{ once: true }}
                      sx={{
                        backgroundColor: 'rgba(255,255,255,0.05)',
                        border: `2px solid ${ACCENT}`,
                      }}
                    >
                      {React.cloneElement(step.icon, {
                        sx: { fontSize: 28, color: ACCENT },
                      })}
                    </StepDot>

                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, mt: 1, textAlign: 'center' }}
                    >
                      {step.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(255,255,255,0.7)',
                        whiteSpace: 'pre-line', 
                        mt: 1,
                        maxWidth: 280,
                        textAlign: 'center',
                        lineHeight: 1.6,
                      }}
                    >
                      {step.desc}
                    </Typography>

                    {i < howItWorksUsers.length - 1 && (
                      <ConnectorLine
                        component={motion.div}
                        initial={{ height: 0, opacity: 0 }}
                        whileInView={{ height: 60, opacity: 1 }}
                        transition={{ duration: 1.0, delay: i * 0.3 + 0.6 }}
                        viewport={{ once: true }}
                        sx={{ borderLeft: `2px dashed ${ACCENT}` }}
                      />
                    )}
                  </React.Fragment>
                ))}
              </Stack>
            </FlowContainer>


            {/* Developers Flow */}
            <FlowContainer className="flow-container" sx={{ maxWidth: { xs: '100%', md: 360 } }}>
              <Typography
                component={motion.h5}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                variant="h5"
                align="center"
                sx={{
                  color: '#fff',
                  WebkitTextStroke: `1px ${ACCENT2}`,
                  WebkitTextFillColor: '#fff',
                  fontWeight: 700,
                  mb: 2,
                }}
              >
                For Developers
              </Typography>
              

              <Stack alignItems="center">
                {howItWorksDevs.map((step, i) => (
                  <React.Fragment key={i}>
                    <StepDot
                      component={motion.div}
                      initial={{ scale: 0, opacity: 0 }}
                      whileInView={{ scale: 1, opacity: 1 }}
                      transition={{
                        type: 'spring',
                        bounce: 0.3,
                        duration: 0.8,
                        delay: i * 0.3 + 0.6,
                      }}
                      viewport={{ once: true }}
                      sx={{
                        backgroundColor: 'rgba(255,255,255,0.05)',
                        border: `2px solid ${ACCENT2}`,
                      }}
                    >
                      {React.cloneElement(step.icon, {
                        sx: { fontSize: 28, color: ACCENT2 },
                      })}
                    </StepDot>

                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, mt: 1, textAlign: 'center' }}
                    >
                      {step.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(255,255,255,0.7)',
                        mt: 1,
                        maxWidth: 280,
                        textAlign: 'center',
                        lineHeight: 1.6,
                      }}
                    >
                      {step.desc}
                    </Typography>
                    

                    {i < howItWorksDevs.length - 1 && (
                      <ConnectorLine
                        component={motion.div}
                        initial={{ height: 0, opacity: 0 }}
                        whileInView={{ height: 60, opacity: 1 }}
                        transition={{ duration: 1.0, delay: i * 0.3 + 0.8 }}
                        viewport={{ once: true }}
                        sx={{ borderLeft: `2px dashed ${ACCENT2}` }}
                      />
                    )}
                  </React.Fragment>
                ))}
              </Stack>
            </FlowContainer>
          </Box>
        </Container>
      </Box>


            {/* Built‑in Agents */}
            <Box
        component={motion.div}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        viewport={{ once: true }}
        sx={{
          py: { xs: 8, md: 12 },
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Container maxWidth="lg">
        <SectionTitle
  component={motion.h3}
  initial={{ opacity: 0, y: 10 }}
  whileInView={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.6, delay: 0.3 }}
  viewport={{ once: true }}
  variant="h3"
  align="center"
  sx={{
    WebkitTextStroke: `1px ${ACCENT2}`,
    WebkitTextFillColor: '#fff',
    color: 'transparent',
    mb: 6,
  }}
>
  Scout also offers built‑in agents
</SectionTitle>

          <Grid container spacing={{ xs: 3, md: 4 }} justifyContent="center">
            {[
              {
                icon: <SearchIcon sx={{ fontSize: 40 }} />,
                title: 'Search Agent',
                desc: 'Instantly search across all your apps, docs & emails.',
              },
              {
                icon: <FolderOpenIcon sx={{ fontSize: 40 }} />,
                title: 'File Organizer Agent',
                desc: 'Automatically sort, tag & clean up your files.',
              },
              {
                icon: <SmartToyIcon sx={{ fontSize: 40 }} />,
                title: 'More Coming Soon!',
                desc: 'We’re building more helpers, stay tuned!',
              },
            ].map((agent, i) => (
              <Grid key={i} item xs={12} sm={6} md={4} display="flex" justifyContent="center">
              <Card
                component={motion.div}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 + i * 0.2 }}
                viewport={{ once: true }}
                variant="outlined"
                className="agent-card"
                sx={{
                  width: { xs: '100%', sm: 280 },
                  maxWidth: '100%',
                  bgcolor: '#111',
                  borderColor: '#222',
                  borderRadius: 2,
                  p: { xs: 2.5, sm: 3 },
                  textAlign: 'center',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 4px 12px ${ACCENT2}33`,
                }}
              >
                <Box sx={{ color: ACCENT2, mb: 2 }}>{agent.icon}</Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  {agent.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {agent.desc}
                </Typography>
              </Card>
            </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
     
{/* ────────── Before Scout → With Scout ────────── */}
<Box
  component={motion.div}
  initial={{ opacity: 0, y: 20 }}
  whileInView={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.8 }}
  viewport={{ once: true, amount: 0 }}
  sx={{
    py: { xs: 8, md: 16 },
    position: 'relative',
    zIndex: 1,
  }}
>
  <Container maxWidth="lg">
    {/* ───── For Users ───── */}
    <Stack className="user-comparison" spacing={4} alignItems="center" sx={{ mb: 12 }}>

      <SectionTitle
      component={motion.h3}
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.3 }}
      viewport={{ once: true }}
      variant="h3"
      align="center"
      sx={{
        WebkitTextStroke: `1px ${ACCENT2}`,
        WebkitTextFillColor: '#fff',
        color: 'transparent',
        mb: 6,
      }}
    >
      
        
        For Users
      </SectionTitle>

      {/* desktop-only header row */}
      <Box
        className="comparison-header"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
          width: '100%',
        }}
      >
        <Box sx={{ width: { xs: '100%', sm: 320, md: 380 }, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ color: ACCENT2 }}>Before Scout</Typography>
        </Box>
        
        <Box sx={{ width: { xs: '100%', sm: 320, md: 380 }, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ color: ACCENT2 }}>With Scout</Typography>
        </Box>
      </Box>

      {userFacingProblems.map((p, i) => (
        <Box
          key={i}
          component={motion.div}
          className="comparison-row"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: i * 0.2 }}
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            width: '100%',
          }}
        >
          {/* Past */}
          <Card
            className="comparison-card"
            sx={{
              position: 'relative',
              width: { xs: '100%', sm: 320, md: 380 },
              bgcolor: 'rgba(218,96,91,0.06)',
              border: `1px solid ${ACCENT2}`,
              p: { xs: 2.5, md: 2 },
            }}
          >
            <PersonIcon sx={{ position: 'absolute', top: 8, right: 8, color: ACCENT2 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: ACCENT2 }}>
              {p.title}
            </Typography>
            <Typography sx={{ color: '#ccc', lineHeight: 1.6 }}>
              {p.past}
            </Typography>
          </Card>

          {/* Arrow */}
          <ArrowForwardIcon
            className="comparison-arrow"
            sx={{
              fontSize: 30,
              color: ACCENT2,
              mx: { xs: 'auto', md: 0 },
              my: { xs: 1, md: 0 },
            }}
          />

          {/* Future */}
          <Card
            className="comparison-card"
            sx={{
              width: { xs: '100%', sm: 320, md: 380 },
              bgcolor: 'rgba(218,96,91,0.02)',
              border: `1px solid ${ACCENT2}`,
              p: 2,
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: ACCENT2 }}>
              {p.futureTitle}
            </Typography>
            <Typography sx={{ color: '#ccc', lineHeight: 1.6 }}>
              {p.future}
            </Typography>
          </Card>
        </Box>
      ))}
    </Stack>

    {/* ───── For Developers ───── */}
    <Stack className="dev-comparison" spacing={4} alignItems="center">
    <SectionTitle
      component={motion.h3}
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.3 }}
      viewport={{ once: true }}
      variant="h3"
      align="center"
      sx={{
        WebkitTextStroke: `1px ${ACCENT}`,
        WebkitTextFillColor: '#fff',
        color: 'transparent',
        mb: 6,
      }}
    >
       
        
        For Developers
      </SectionTitle>

      <Box
        className="comparison-header"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
          width: '100%',
        }}
      >
        <Box sx={{ width: { xs: '100%', sm: 320, md: 380 }, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ color: ACCENT }}>Before Scout</Typography>
        </Box>
        
        <Box sx={{ width: { xs: '100%', sm: 320, md: 380 }, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ color: ACCENT }}>With Scout</Typography>
        </Box>
      </Box>

      {developerFacingProblems.map((p, i) => (
        <Box
          key={i}
          component={motion.div}
          className="comparison-row"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: i * 0.2 }}
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            width: '100%',
          }}
        >
          {/* Past */}
          <Card
            className="comparison-card"
            sx={{
              position: 'relative',
              width: { xs: '100%', sm: 320, md: 380 },
              bgcolor: 'rgba(70,126,229,0.06)',
              border: `1px solid ${ACCENT}`,
              p: 2,
            }}
          >
            <CodeIcon sx={{ position: 'absolute', top: 8, right: 8, color: ACCENT }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: ACCENT }}>
              {p.title}
            </Typography>
            <Typography sx={{ color: '#ccc', lineHeight: 1.6 }}>
              {p.past}
            </Typography>
          </Card>

          {/* Arrow */}
          <ArrowForwardIcon
            className="comparison-arrow"
            sx={{
              fontSize: 30,
              color: ACCENT,
              mx: { xs: 'auto', md: 0 },
              my: { xs: 1, md: 0 },
            }}
          />

          {/* Future */}
          <Card
            className="comparison-card"
            sx={{
              width: { xs: '100%', sm: 320, md: 380 },
              bgcolor: 'rgba(70,126,229,0.02)',
              border: `1px solid ${ACCENT}`,
              p: 2,
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: ACCENT }}>
              {p.futureTitle}
            </Typography>
            <Typography sx={{ color: '#ccc', lineHeight: 1.6 }}>
              {p.future}
            </Typography>
          </Card>
        </Box>
      ))}
    </Stack>
  </Container>
</Box>


      {/* Join Waitlist Banner */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        viewport={{ once: true }}
        className="waitlist-banner"
        sx={{
          py: { xs: 6, md: 8 },
          bgcolor: '#111',
          textAlign: 'center',
          mt: 8,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            mb: 3,
          }}
        >
          Ready to get started?
        </Typography>
        <Button
          component={RouterLink}
          to="/waitlist"
          variant="contained"
          size="large"
          className="waitlist-button"
          sx={{
            px: 6,
            py: 2,
            fontSize: '1rem',
            fontWeight: 700,
            borderRadius: 999,
            bgcolor: ACCENT,
            
          }}
        >
          Join the Waitlist
        </Button>
      </Box>
    </MainWrapper>
  </MobileResponsiveWrapper>
  )
}
